/*
 * mr-delivery-dates.js
 *
 * Carries the delivery dates calculated in Liquid onto the cart, so that
 * Shopify Flow can copy them onto the order.
 *
 * This file deliberately contains NO date arithmetic. Every date it handles
 * was calculated by snippets/mr-delivery-dates.liquid and fetched through
 * the Section Rendering API. If you find yourself adding a Date() here,
 * stop - that is a second source of truth and the whole point of this
 * rewrite was to have one.
 *
 * Flow of control:
 *   1. Ask /?sections=mr-delivery-dates for the current cart's dates.
 *   2. Compare against the cart attributes already set.
 *   3. POST /cart/update.js only if they differ.
 *
 * 19 Oct 2026: the section can now answer {"clear": true} - a backorder in
 * the basket with no usable due date. The attributes are then set to empty
 * so the order carries NO promised date rather than a stale one left over
 * from an earlier basket.
 */

(function () {
  'use strict';

  if (window.__mrDeliveryDatesLoaded) return;
  window.__mrDeliveryDatesLoaded = true;

  var SECTION_ID = 'mr-delivery-dates';
  var ATTRIBUTE_KEYS = ['fulfill_by', 'delivery_date'];

  var syncing = false;
  var queued = false;

  function fetchCalculatedDates() {
    return fetch('/?sections=' + SECTION_ID, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Section fetch failed: ' + response.status);
        return response.json();
      })
      .then(function (sections) {
        var html = sections[SECTION_ID];
        if (!html) return null;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var holder = parsed.querySelector('[data-mr-delivery-dates]');
        if (!holder) return null;

        var text = (holder.textContent || '').trim();
        if (!text) return null;

        var data = JSON.parse(text);
        if (data && data.clear) return { fulfill_by: '', delivery_date: '' };
        return data && data.fulfill_by ? data : null;
      });
  }

  function fetchCartAttributes() {
    return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart fetch failed: ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        return { count: cart.item_count, attributes: cart.attributes || {} };
      });
  }

  function needsUpdate(current, calculated) {
    return ATTRIBUTE_KEYS.some(function (key) {
      return (current[key] || '') !== (calculated[key] || '');
    });
  }

  function writeAttributes(calculated) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        attributes: {
          fulfill_by: calculated.fulfill_by,
          delivery_date: calculated.delivery_date
        }
      })
    });
  }

  function sync() {
    // A cart update of our own fires cart change events, which would call
    // sync again. Collapse that into a single follow-up run.
    if (syncing) {
      queued = true;
      return;
    }
    syncing = true;

    fetchCartAttributes()
      .then(function (cart) {
        if (!cart.count) return null;

        return fetchCalculatedDates().then(function (calculated) {
          if (!calculated) return null;
          if (!needsUpdate(cart.attributes, calculated)) return null;
          return writeAttributes(calculated);
        });
      })
      .catch(function (error) {
        // Never block checkout over this. A missing attribute means Flow
        // leaves the metafields empty, which is visible and fixable; a
        // thrown error in a checkout path is neither.
        console.warn('[mr-delivery-dates] sync skipped:', error.message);
      })
      .then(function () {
        syncing = false;
        if (queued) {
          queued = false;
          setTimeout(sync, 0);
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  // Theme cart events vary by theme version, so listen broadly rather than
  // betting on one name.
  ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh', 'CartUpdateEvent'].forEach(
    function (eventName) {
      document.addEventListener(eventName, function () {
        setTimeout(sync, 150);
      });
    }
  );

  // Belt and braces: if the tab has been sitting open across midnight, the
  // dates are stale. Re-sync when the customer comes back to it.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) sync();
  });
})();
